import { useId, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { DESIGNS_PROPOSES, designsAffiches, designDescriptions, designNames } from './designs.js';
import { useDesign } from './useDesign.js';
import styles from './DesignPicker.module.css';

export interface DesignPickerProps {
  compteId?: string | null;
  langue?: 'fr' | 'en';
}
/** Native radio controls preserve keyboard behaviour, labels and selected state. */
export function DesignPicker({ compteId, langue }: DesignPickerProps): JSX.Element {
  const { i18n } = useTranslation();
  const language =
    langue ?? ((i18n.resolvedLanguage ?? i18n.language).startsWith('en') ? 'en' : 'fr');
  const { design, setDesign } = useDesign(compteId);
  const id = useId();
  return (
    <fieldset className={styles.picker} data-ui="design-picker">
      <legend>{language === 'en' ? 'Interface design' : 'Design de l’interface'}</legend>
      <div className={styles.options}>
        {designsAffiches(design).map((option) => (
          <label
            key={option}
            className={styles.option}
            data-selected={design === option}
            data-option={option}
          >
            <input
              type="radio"
              aria-label={designNames[option]}
              name={id}
              value={option}
              checked={design === option}
              onChange={() => setDesign(option)}
              aria-describedby={`${id}-${option}`}
            />
            <span className={styles.preview} aria-hidden="true">
              <i />
              <b />
              <b />
              <b />
            </span>
            <span className={styles.copy}>
              <strong>
                {designNames[option]}
                {DESIGNS_PROPOSES.includes(option)
                  ? ''
                  : language === 'en'
                    ? ' (no longer offered)'
                    : ' (plus proposé)'}
              </strong>
              <span id={`${id}-${option}`}>{designDescriptions[language][option]}</span>
            </span>
            <Check size={17} className={styles.check} aria-hidden="true" />
          </label>
        ))}
      </div>
      <p className={styles.note}>
        {language === 'en'
          ? 'Saved for your account in this browser. Light, dark and fair mode remain independent.'
          : 'Enregistré pour votre compte dans ce navigateur. Les modes clair, sombre et foire restent indépendants.'}
      </p>
    </fieldset>
  );
}
